import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router'
import { subscribeRealtimeEvents, type RealtimeEvent } from '../lib/api.js'
import { useAuth } from '../contexts/AuthContext.js'

/** Custom DOM event fired whenever the bridge wake-word triggers. */
export const WAKE_EVENT = 'stark.wake'

/**
 * Mount once at the App level. Opens the SSE channel, dispatches wake events
 * onto window so any page (or VoiceModeOverlay specifically) can react, AND
 * navigates the user to the chat with ?voice=1 so the voice overlay opens
 * even if they were on /settings or /tasks when they said "Stark".
 *
 * Reconnects with a 3 s backoff if the stream ends — a network blip or proxy
 * timeout shouldn't stop wake detection for the rest of the session.
 */
export function useRealtimeEvents(): void {
  const { user } = useAuth()
  const navigate = useNavigate()
  const aliveRef = useRef(true)

  useEffect(() => {
    if (!user) return
    aliveRef.current = true
    let abort = new AbortController()

    const handleEvent = (event: RealtimeEvent): void => {
      if (event.type === 'wake') {
        try { window.dispatchEvent(new CustomEvent(WAKE_EVENT, { detail: event })) } catch { /* noop */ }
        // If the user is anywhere but /chat, route them there with the auto-
        // open flag. ChatPage handles the param and clears it.
        if (window.location.pathname !== '/') {
          navigate('/?voice=1', { replace: false })
        }
      }
    }

    const loop = async (): Promise<void> => {
      while (aliveRef.current) {
        try {
          abort = new AbortController()
          await subscribeRealtimeEvents({ signal: abort.signal, onEvent: handleEvent })
        } catch (e) {
          if (!aliveRef.current) return
          console.warn('[realtime] stream ended, retrying in 3s:', e instanceof Error ? e.message : e)
        }
        await new Promise((r) => setTimeout(r, 3000))
      }
    }
    void loop()

    return () => {
      aliveRef.current = false
      abort.abort()
    }
  }, [user, navigate])
}
